import { createContext, useContext, useEffect, useState, useMemo, ReactNode } from 'react';
import { useStore } from './store';

interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
  photo_url?: string;
}

interface TelegramTheme {
  bg_color: string;
  text_color: string;
  hint_color: string;
  link_color: string;
  button_color: string;
  button_text_color: string;
  secondary_bg_color: string;
}

interface TelegramContextType {
  webApp: typeof window.Telegram.WebApp | null;
  user: TelegramUser | null;
  theme: TelegramTheme | null;
  initData: string;
  isReady: boolean;
  environment: 'telegram' | 'pwa' | 'web';
  colorScheme: 'light' | 'dark';
  showMainButton: (text: string, onClick: () => void) => void;
  hideMainButton: () => void;
  showBackButton: (onClick: () => void) => void;
  hideBackButton: () => void;
  showAlert: (message: string) => void;
  showConfirm: (message: string) => Promise<boolean>;
  hapticFeedback: (type: 'impact' | 'notification' | 'selection') => void;
  close: () => void;
  expand: () => void;
}

const TelegramContext = createContext<TelegramContextType | null>(null);

declare global {
  interface Window {
    Telegram: {
      WebApp: {
        ready: () => void;
        expand: () => void;
        close: () => void;
        initData: string;
        initDataUnsafe: {
          user?: TelegramUser;
          start_param?: string;
        };
        themeParams: TelegramTheme;
        colorScheme: 'light' | 'dark';
        isVerticalSwipesEnabled: boolean;
        MainButton: {
          text: string;
          show: () => void;
          hide: () => void;
          onClick: (callback: () => void) => void;
          offClick: (callback: () => void) => void;
          setText: (text: string) => void;
          enable: () => void;
          disable: () => void;
        };
        BackButton: {
          show: () => void;
          hide: () => void;
          onClick: (callback: () => void) => void;
          offClick: (callback: () => void) => void;
        };
        HapticFeedback: {
          impactOccurred: (style: string) => void;
          notificationOccurred: (type: string) => void;
          selectionChanged: () => void;
        };
        showAlert: (message: string, callback?: () => void) => void;
        showConfirm: (message: string, callback: (confirmed: boolean) => void) => void;
        setHeaderColor: (color: string) => void;
        setBackgroundColor: (color: string) => void;
        enableClosingConfirmation: () => void;
        disableClosingConfirmation: () => void;
      };
    };
  }
}

export function TelegramProvider({ children }: { children: ReactNode }) {
  const [isReady, setIsReady] = useState(false);
  const [webApp, setWebApp] = useState<typeof window.Telegram.WebApp | null>(null);
  const [environment, setEnvironment] = useState<'telegram' | 'pwa' | 'web'>('web');
  const [colorScheme, setColorScheme] = useState<'light' | 'dark'>('light');
  const { settings } = useStore();

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    const isStandalone = window.matchMedia?.('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone;

    if (tg) {
      tg.ready();
      tg.expand();

      // Enable closing confirmation to prevent accidental closes
      tg.enableClosingConfirmation();

      // Lock viewport to prevent scrolling issues
      tg.isVerticalSwipesEnabled = false;

      setWebApp(tg);
      setIsReady(true);
      setEnvironment('telegram');
      setColorScheme(tg.colorScheme || 'light');

      // Set theme colors
      if (tg.themeParams.bg_color) {
        tg.setBackgroundColor(tg.themeParams.bg_color);
      }
      if (tg.themeParams.secondary_bg_color) {
        tg.setHeaderColor(tg.themeParams.secondary_bg_color);
      }
    } else {
      // Standalone/PWA or web fallback
      setEnvironment(isStandalone ? 'pwa' : 'web');
      setIsReady(true);
    }
  }, []);

  useEffect(() => {
    const resolvedScheme =
      settings.theme === 'system'
        ? (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
        : settings.theme;
    const scheme = resolvedScheme || webApp?.colorScheme || colorScheme || 'light';
    document.documentElement.setAttribute('data-theme', scheme);
    document.documentElement.lang = settings.language || 'en';
    if (webApp?.themeParams?.bg_color && environment === 'telegram') {
      document.body.style.backgroundColor = webApp.themeParams.bg_color;
    } else {
      document.body.style.backgroundColor = getComputedStyle(document.documentElement)
        .getPropertyValue('--tg-theme-bg-color') || '#f5f5f7';
    }
  }, [webApp, colorScheme, settings.theme, settings.language, environment]);

  const showMainButton = (text: string, onClick: () => void) => {
    if (webApp?.MainButton) {
      webApp.MainButton.setText(text);
      webApp.MainButton.onClick(onClick);
      webApp.MainButton.show();
      webApp.MainButton.enable();
    }
  };

  const hideMainButton = () => {
    if (webApp?.MainButton) {
      webApp.MainButton.hide();
    }
  };

  const showBackButton = (onClick: () => void) => {
    if (webApp?.BackButton) {
      webApp.BackButton.onClick(onClick);
      webApp.BackButton.show();
    }
  };

  const hideBackButton = () => {
    if (webApp?.BackButton) {
      webApp.BackButton.hide();
    }
  };

  const showAlert = (message: string) => {
    if (webApp) {
      webApp.showAlert(message);
    } else {
      alert(message);
    }
  };

  const showConfirm = (message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      if (webApp) {
        webApp.showConfirm(message, resolve);
      } else {
        resolve(confirm(message));
      }
    });
  };

  const hapticFeedback = (type: 'impact' | 'notification' | 'selection') => {
    if (webApp?.HapticFeedback) {
      switch (type) {
        case 'impact':
          webApp.HapticFeedback.impactOccurred('medium');
          break;
        case 'notification':
          webApp.HapticFeedback.notificationOccurred('success');
          break;
        case 'selection':
          webApp.HapticFeedback.selectionChanged();
          break;
      }
    }
  };

  const close = () => {
    webApp?.close();
  };

  const expand = () => {
    webApp?.expand();
  };

  const value: TelegramContextType = useMemo(() => ({
    webApp,
    user: webApp?.initDataUnsafe?.user || null,
    theme: webApp?.themeParams || null,
    initData: webApp?.initData || '',
    isReady,
    environment,
    colorScheme: webApp?.colorScheme || colorScheme,
    showMainButton,
    hideMainButton,
    showBackButton,
    hideBackButton,
    showAlert,
    showConfirm,
    hapticFeedback,
    close,
    expand,
  }), [webApp, isReady]);

  return (
    <TelegramContext.Provider value={value}>
      {children}
    </TelegramContext.Provider>
  );
}

export function useTelegram() {
  const context = useContext(TelegramContext);
  if (!context) {
    throw new Error('useTelegram must be used within TelegramProvider');
  }
  return context;
}

export default TelegramProvider;
